/**
 * Le volcan de poche (author's addition): put like the geyser, on the ground under the point. A little cone of
 * obsidian rises there; at the start of each of the next VOLCANO_TURNS turns it erupts (lava bombs and a pour of lava
 * from its top), and after the last eruption the cone itself melts into lava. In the sandbox it erupts every
 * VOLCANO_SANDBOX_TICKS. Its blows are its owner's team's (friendly fire).
 */
import { Physique, STATE_STATIC, STATE_POWDER, STATE_LIQUID, wakeRect } from '@invaders/sim';
import { TICKS_PER_SECOND, type GameState, type Volcano } from './state';
import { chestAt } from './chests';
import { unitAt } from './weapons';
import { throwLavaBombs } from './maps/events';

const { LAVA, OBSIDIAN } = Physique;

export const VOLCANO_TURNS = 3;
export const VOLCANO_SANDBOX_TICKS = 3 * TICKS_PER_SECOND;
/** The cone: its half width at the base, and its height. Values chosen. */
export const VOLCANO_HALF = 20;
export const VOLCANO_HEIGHT = 18;
/** Each eruption. Values chosen. */
export const VOLCANO_BOMBS = 10;
export const VOLCANO_POUR = 120;

/** Half width of the cone at `up` rows above the ground. */
const coneHalf = (up: number) => Math.trunc((VOLCANO_HALF * (VOLCANO_HEIGHT - up)) / VOLCANO_HEIGHT);

/** A volcano at the point: on the first solid or powder ground under it. */
export function startVolcano(state: GameState, x: number, y: number, owner: number): void {
  const { w, h, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const matState = state.world.rules.materials.state;
  const cx = Math.min(w - 1, Math.max(0, x));
  let gy = Math.max(0, y);
  while (gy < h && matState[data[gy * w + cx]] !== STATE_STATIC && matState[data[gy * w + cx]] !== STATE_POWDER) gy++;
  for (let up = 1; up <= VOLCANO_HEIGHT; up++) {
    const yy = gy - up;
    if (yy < 0) break;
    const half = coneHalf(up);
    for (let xx = Math.max(0, cx - half); xx <= Math.min(w - 1, cx + half); xx++) {
      const i = yy * w + xx;
      if (matState[data[i]] < STATE_LIQUID || unitAt(state, xx, yy, -1) || chestAt(state, xx, yy) >= 0) continue;
      data[i] = OBSIDIAN;
      cells[i] = 0;
    }
  }
  wakeRect(state.world, cx - VOLCANO_HALF, gy - VOLCANO_HEIGHT, cx + VOLCANO_HALF, gy);
  state.volcanoes.push({ x: cx, y: gy, owner, turns: VOLCANO_TURNS, tick: 0 });
}

/** An eruption: bombs in a bell, lava poured on the top, which runs down the cone. */
function eruption(state: GameState, v: Volcano): void {
  const { w, data } = state.world.physique;
  const cells = state.world.cellState.data;
  const matState = state.world.rules.materials.state;
  const top = v.y - VOLCANO_HEIGHT - 1;
  throwLavaBombs(state, v.x, top - 2, VOLCANO_BOMBS, v.owner);
  let left = VOLCANO_POUR;
  for (let y = top; y >= 0 && y >= top - 12 && left > 0; y--) {
    for (let x = Math.max(0, v.x - 6); x <= Math.min(w - 1, v.x + 6) && left > 0; x++) {
      const i = y * w + x;
      if (matState[data[i]] <= STATE_LIQUID || unitAt(state, x, y, -1)) continue;
      data[i] = LAVA;
      cells[i] = 0;
      left--;
    }
  }
  wakeRect(state.world, v.x - 8, Math.max(0, top - 13), v.x + 8, top + 1);
  state.events.push({ type: 'eruption', x: v.x, y: top });
}

/** The last eruption is over: what is left of the cone turns into lava. */
function melt(state: GameState, v: Volcano): void {
  const { w, data } = state.world.physique;
  const cells = state.world.cellState.data;
  for (let up = 1; up <= VOLCANO_HEIGHT; up++) {
    const y = v.y - up;
    if (y < 0) break;
    const half = coneHalf(up);
    for (let x = Math.max(0, v.x - half); x <= Math.min(w - 1, v.x + half); x++) {
      if (data[y * w + x] === OBSIDIAN) { data[y * w + x] = LAVA; cells[y * w + x] = 0; }
    }
  }
  wakeRect(state.world, v.x - VOLCANO_HALF, v.y - VOLCANO_HEIGHT, v.x + VOLCANO_HALF, v.y);
}

function erupt(state: GameState, index: number): void {
  const v = state.volcanoes[index];
  eruption(state, v);
  if (--v.turns > 0) return;
  melt(state, v);
  state.volcanoes.splice(index, 1);
}

/** Start of a turn (match). */
export function volcanoesTurnStart(state: GameState): void {
  for (let i = state.volcanoes.length - 1; i >= 0; i--) erupt(state, i);
}

/** In the sandbox the eruptions come with time. */
export function updateVolcanoes(state: GameState): void {
  if (state.match) return;
  for (let i = state.volcanoes.length - 1; i >= 0; i--) {
    const v = state.volcanoes[i];
    if (++v.tick >= VOLCANO_SANDBOX_TICKS) { v.tick = 0; erupt(state, i); }
  }
}
